import type { ImageLoader } from 'next/image'

import type { Media as MediaType } from '@/payload-types'

import { getMediaUrl } from '@/utilities/getMediaUrl'

/**
 * SRCSET SEM O OTIMIZADOR DO NEXT — a partir dos tamanhos que o PAYLOAD já gerou.
 *
 * ── POR QUE (performance, 19/08/2026) ──
 * A mídia vive no R2 atrás da Cloudflare, e o `/_next/image` levava 403 ao buscá-la do servidor
 * (ver `project_imagem_next_optimizer_r2_403`). A solução da época foi `unoptimized` para toda URL
 * remota — correta, mas com um efeito colateral: ZERO `srcset` no site inteiro. Um card de 100 px na
 * home baixava o original de 1200 px; medido: 43 imagens na home, nenhuma com srcset.
 *
 * O Payload já grava `thumbnail 300 · small 600 · medium 900 · large 1400 · xlarge 1920` para cada
 * upload, com URL pública direta. Um `loader` próprio devolve, para cada largura que o Next pedir, a
 * URL do MENOR tamanho gerado que ainda a cobre — o Next monta o `srcset`, o browser escolhe pelo
 * `sizes` do chamador, e o `/_next/image` continua fora do caminho (zero 403).
 *
 * ── O QUE NÃO MUDA ──
 *  · O `src` (largura máxima pedida) continua sendo o ORIGINAL — quem não lê srcset (crawler, no-JS,
 *    Discover, JSON-LD) vê o mesmo de sempre.
 *  · Só entram tamanhos que PRESERVAM A PROPORÇÃO (na config só têm `width`). Os crops (`square`,
 *    `og`, `editorial`, `authorAvatar`) mudariam o enquadramento de um `object-cover` — ficam fora.
 *    Por segurança, cada candidato ainda é conferido contra a proporção do original (±2%).
 *  · Imagem sem tamanhos gerados (importações antigas, SVG, GIF), URL local ou string solta: o
 *    chamador NÃO usa este loader e o comportamento fica idêntico ao de antes.
 *
 * ── HONESTIDADE DO DESCRITOR ──
 * O Next pede larguras fixas (16…3840). Acima do maior tamanho gerado devolvemos o original — o
 * descritor `1920w` pode então apontar para 1200 px reais. É exatamente o que o site já servia para
 * TODOS os tamanhos (o original), então não há perda em relação a hoje; só ganho abaixo disso.
 */

/** Tamanhos da coleção Media definidos só com `width` — mantêm a proporção do original. */
export const TAMANHOS_PROPORCIONAIS = ['thumbnail', 'small', 'medium', 'large', 'xlarge'] as const

export type CandidatoDeTamanho = { width: number; url: string }

type SizeLike = { url?: string | null; width?: number | null; height?: number | null }

/**
 * Lista ordenada (largura crescente, sem repetição) dos tamanhos utilizáveis + o original no fim.
 * Devolve `[]` se não houver pelo menos UMA alternativa ao original — o chamador então não liga o loader.
 */
export function candidatosDeSrcset(
  resource: Pick<MediaType, 'url' | 'width' | 'height' | 'sizes' | 'updatedAt'> | null | undefined,
  srcOriginal: string,
): CandidatoDeTamanho[] {
  if (!resource || !srcOriginal) return []
  const cacheTag = resource.updatedAt
  const larguraOriginal = typeof resource.width === 'number' ? resource.width : 0
  const alturaOriginal = typeof resource.height === 'number' ? resource.height : 0
  const proporcao = larguraOriginal > 0 && alturaOriginal > 0 ? larguraOriginal / alturaOriginal : 0

  const porLargura = new Map<number, string>()
  const sizes = (resource.sizes ?? {}) as Record<string, SizeLike | undefined>
  for (const nome of TAMANHOS_PROPORCIONAIS) {
    const s = sizes[nome]
    if (!s?.url || typeof s.width !== 'number' || s.width <= 0) continue
    // Tamanho «gerado» na largura do original é o próprio original — não acrescenta candidato.
    if (larguraOriginal > 0 && s.width >= larguraOriginal) continue
    // Rede de segurança: se a config um dia ganhar `height` num destes, o crop fica de fora.
    if (proporcao > 0 && typeof s.height === 'number' && s.height > 0) {
      const p = s.width / s.height
      if (Math.abs(p - proporcao) / proporcao > 0.02) continue
    }
    if (!porLargura.has(s.width)) porLargura.set(s.width, getMediaUrl(s.url, cacheTag))
  }
  if (porLargura.size === 0) return []

  const lista = [...porLargura.entries()]
    .map(([width, url]) => ({ width, url }))
    .sort((a, b) => a.width - b.width)
  lista.push({
    width: larguraOriginal > 0 ? larguraOriginal : Number.MAX_SAFE_INTEGER,
    url: srcOriginal,
  })
  return lista
}

/** Loader do `next/image`: menor candidato que cobre a largura pedida; acima de todos, o original. */
export function loaderDeCandidatos(candidatos: CandidatoDeTamanho[]): ImageLoader {
  return ({ width }) => {
    for (const c of candidatos) if (c.width >= width) return c.url
    return candidatos[candidatos.length - 1]!.url
  }
}

/**
 * Cache por referência da lista → o MESMO `loader` para a mesma lista de candidatos.
 *
 * Sem isto, chamar o helper duas vezes no mesmo JSX (uma no `loader`, outra no `unoptimized`)
 * criaria funções diferentes a cada render e o `next/image` remontaria a imagem à toa. Com o
 * WeakMap, a resposta é estável e a entrada morre junto com a lista.
 */
const loadersPorLista = new WeakMap<object, ImageLoader>()

/** `loader` pronto para o `next/image`, ou `undefined` quando não há alternativa ao original. */
export function loaderDeSizesOpcional(
  candidatos: CandidatoDeTamanho[] | null | undefined,
): ImageLoader | undefined {
  if (!candidatos || candidatos.length < 2) return undefined
  const cacheado = loadersPorLista.get(candidatos)
  if (cacheado) return cacheado
  const loader = loaderDeCandidatos(candidatos)
  loadersPorLista.set(candidatos, loader)
  return loader
}
